<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>{{ doc ? 'Update' : 'New' }} Legal Document</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <v-text-field v-model="formData['title']" autofocus
                                          :rules="[vRules.required,vRules.maxLength(50)]"
                                          density="compact" label="Title"/>
                        </v-col>
                        <v-col class="py-0" cols="6">
                            <v-text-field v-model="formData['version']"
                                          :rules="[vRules.required,vRules.maxLength(50)]"
                                          density="compact" label="Version"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <!-- The extensions are listed alongside the MIME types because Windows
                                 does not always map .html to text/html in the file picker. -->
                            <v-file-input v-model="formData['doc_file']" :rules="props.doc ? [] : [vRules.required]"
                                          accept="application/pdf,text/html,.pdf,.html,.htm" density="compact"
                                          hint="PDF or HTML, up to 2 MB" persistent-hint
                                          label="Document File"/>
                        </v-col>
                        <v-col class="py-0" cols="6">
                            <v-text-field v-model="formData['doc_key']"
                                          :rules="[vRules.required,vRules.maxLength(50)]"
                                          density="compact" hint="terms_of_service" label="Document Key"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <v-date-input
                                v-model="formData['effective_date']" density="compact"
                                hide-actions label="Effective Date" prepend-icon=""/>
                        </v-col>
                        <v-col class="py-0" cols="6">
                            <v-date-input
                                v-model="formData['expiry_date']" density="compact"
                                hide-actions label="Expiry Date" prepend-icon=""/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <v-select v-model="formData['status']"
                                      :items="[{value:true,title:'Active'},{value:false,title:'Inactive'}]"
                                      :rules="[vRules.required]" density="compact" label="Status"/>
                        </v-col>
                        <v-col class="py-0" cols="12">
                            <v-textarea v-model="formData['description']"
                                        :rules="[vRules.required,vRules.maxLength(255)]"
                                        density="compact" label="Description"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive} from "vue";
import {send} from "@/composables/apiClient";
import {VDateInput} from 'vuetify/labs/VDateInput'
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    doc: Object,
});
const formData = reactive({
    valid: false,
    doc_file: null,
    title: props.doc?.title ?? null,
    _method: props.doc ? 'put' : 'post',
    description: props.doc?.description ?? null,
    version: props.doc?.version ?? null,
    doc_key: props.doc?.doc_key ?? null,
    effective_date: props.doc?.effective_date ? new Date(props.doc.effective_date) : null,
    expiry_date: props.doc?.expiry_date ? new Date(props.doc.expiry_date) : null,
    status: props.doc ? !!props.doc.status : true,
});

const emits = defineEmits(['update:modelValue', 'addDoc', 'updateDoc']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);
const submitForm = () => {
    errors.value = [];
    if (!formData['effective_date']) {
        snackbar.showSnackbar('Effective date is required.', 'error');
        return;
    }
    if (formData['expiry_date'] && formData['effective_date'] >= formData['expiry_date']) {
        snackbar.showSnackbar('Expiry date must be after the effective date.', 'error');
        return;
    }
    send('post',
        `admin/legal-documents${props.doc ? '/' + props.doc.id : ''}`,
        {
            ...formData,
            effective_date: formatDate(formData['effective_date']),
            expiry_date: formData['expiry_date'] ? formatDate(formData['expiry_date']) : null,
        }, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(props.doc ? 'updateDoc' : 'addDoc', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

function formatDate(dateObj) {
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${dateObj.getFullYear()}-${m}-${d}`;
}
</script>
